import { PrimaryButton } from "@fluentui/react/lib/Button"
import { PropsWithChildren, useRef, useState } from "react"

export type AcceptedFiles =
	| "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
	| "application/vnd.ms-excel"
	| "application/pdf"
	| "application/msword"
	| "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
	| "image/png"
	| "image/jpeg"

interface DragDropFileProps {
	onFilesDropped: (files: FileList) => void
	acceptedFiles?: AcceptedFiles[]
	multiple?: boolean
	previewText?: string
}

export function DragDropFile(props: PropsWithChildren<DragDropFileProps>) {
	const { onFilesDropped, acceptedFiles, multiple, previewText, children } = props
	// drag state
	const [dragActive, setDragActive] = useState(false)
	// ref
	const inputRef = useRef(null)

	// handle drag events
	const handleDrag = function (e) {
		e.preventDefault()
		e.stopPropagation()
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true)
		} else if (e.type === "dragleave") {
			setDragActive(false)
		}
	}

	// triggers when file is dropped
	const handleDrop = function (e) {
		e.preventDefault()
		e.stopPropagation()
		setDragActive(false)
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const dt = new DataTransfer()

			for (let i = 0; i < e.dataTransfer.files.length; i++) {
				const file = e.dataTransfer.files[i]

				if (!acceptedFiles || acceptedFiles.includes(file.type as AcceptedFiles)) {
					dt.items.add(file)
				}
			}
			onFilesDropped(dt.files)
		}
	}

	// triggers when file is selected with click
	const handleChange = function (e) {
		e.preventDefault()
		if (e.target.files && e.target.files.length > 0) {
			const dt = new DataTransfer()

			for (let i = 0; i < e.target.files.length; i++) {
				const file = e.target.files[i]
				if (!acceptedFiles || acceptedFiles.includes(file.type as AcceptedFiles)) {
					dt.items.add(file)
				}
			}
			onFilesDropped(dt.files)
		}
	}

	// triggers the input when the button is clicked
	const onButtonClick = () => {
		inputRef.current.click()
	}

	return (
		<form id="form-file-upload" onDragEnter={handleDrag} onSubmit={e => e.preventDefault()}>
			<input accept={acceptedFiles?.join(",")} ref={inputRef} type="file" id="input-file-upload" multiple={multiple} onChange={handleChange} />
			<div id="label-file-upload" className={dragActive ? "drag-active" : ""} style={{ display: "block" }}>
				{children && <div>{children}</div>}
				<div>
					<p>{previewText || "Drag and drop your file here or"}</p>
					<PrimaryButton onClick={onButtonClick}>Upload a file</PrimaryButton>
				</div>
			</div>
			{dragActive && <div id="drag-file-element" onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}></div>}
		</form>
	)
}
